import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { makeStyles } from '@mui/styles'
import { useFormikContext } from 'formik'
import clsx from 'clsx'

import Tooltip from '@mui/material/Tooltip'
import lang from '@/lang'
import { formatDate, convertDateToApiDateFormat, parseClientDate } from '@/modules/_helpers/formatDate'
import { FillTransition } from '@/modules/base/transitions'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import IconInfo from '@assets/images/icons/info-icon.svg'
import IconCalendar from '@assets/images/icons/calendar.svg'
import RichTooltip from '@/modules/base/components/rich-tooltip/rich-tooltip'
import { applySegmentAutoAdvance } from './date-input.helpers'

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    width: '100%',
  },
  label: {
    textAlign: 'left',
    fontSize: 13,
    marginBottom: 10,
  },
  inputWrapper: {
    position: 'relative',
    width: '100%',
  },
  textInput: {
    background: theme.backgroundColor,
    border: theme.border,
    borderRadius: theme.borderRadius,
    color: theme.textColor,
    height: 45,
    paddingLeft: 15,
    paddingRight: 50,
    width: '100%',
    '&:focus': {
      background: theme.white,
      borderColor: theme.activeInputBorder,
      outline: 'none',
    },
  },
  textInputTall: {
    height: 53,
  },
  textInputError: {
    '&&': {
      borderColor: theme.red,
    },
  },
  pickerButton: {
    alignItems: 'center',
    background: 'transparent',
    border: 'none',
    borderRadius: theme.borderRadius,
    cursor: 'pointer',
    display: 'flex',
    height: '100%',
    justifyContent: 'center',
    padding: 0,
    position: 'absolute',
    right: 0,
    top: 0,
    width: 45,
    zIndex: 2,
    '&:disabled': {
      cursor: 'default',
      opacity: 0.5,
    },
  },
  visuallyHiddenPicker: {
    border: 'none',
    bottom: 0,
    height: 1,
    opacity: 0,
    padding: 0,
    pointerEvents: 'none',
    position: 'absolute',
    right: 20,
    width: 1,
  },
  disabled: {
    '&&': {
      color: theme.disabledColor,
      '&::placeholder': {
        color: theme.disabledColor,
      },
    },
  },
  inline: {
    outline: 'none',
    background: theme.backgroundColor,
    border: '0 !important',
    height: 'auto',
    padding: '3px 0px',
  },
  tooltip: {
    maxWidth: 200,
    marginRight: 50,
  },
  tooltipWithInfoIcon: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 10,
    gap: 10,
    '& > $label': {
      marginBottom: 0,
    },
  },
  error: {
    color: theme.red,
    fontSize: 12,
    marginTop: 10,
  },
}))

interface DateInputProps {
  name?: string;
  // ReactNode, not string: the review marks a flagged field INSIDE its label (see
  // review-field-check.tsx). Widening only — every caller passing a plain string is
  // unaffected, and the render below already just interpolates the value.
  label?: React.ReactNode;
  value?: string | Date;
  minDate?: string | Date;
  maxDate?: string | Date;
  placeholder?: string;
  format?: string;
  useFormik?: boolean;
  labelTooltip?: {
    title?: string;
    description?: string;
    placement?: string;
    showInfoIcon?: boolean;
  };
  tooltip?: string;
  tooltipClass?: string;
  required?: boolean;

  readOnly?: boolean;
  disableClear?: boolean;
  customClass?: string;
  fillAnimationOptions?: {
    show?: boolean;
    color?: string;
  };
  disabled?: boolean;
  dataQsId: string;
  inline?: boolean;
  className?: string;
  tall?: boolean;
  isLoading?: boolean;
  onFocus?: () => void;
  onBlur?: (value: string, name: string) => void;
  onChange?: (value: string | Date, name: string) => void;
  onValidationError?: (value: string, name: string) => void;
  onValidationChange?: (isValid: boolean, name: string) => void;
  onRegisterValidation?: (name: string, validate: (() => boolean) | null) => void;
}

const DateInput = (props: DateInputProps) => {
  const {
    name,
    label,
    labelTooltip,
    readOnly: propsReadOnly,
    required = false,
    customClass,
    inline,
    useFormik = false,
    tooltip = '',
    tooltipClass,
    value,
    className,
    tall,
    minDate = null,
    maxDate = null,
    placeholder = 'TT.MM.JJJJ',
    format = 'DD.MM.YYYY',
    isLoading = false,
    disableClear,
    fillAnimationOptions,
    onChange,
    onFocus,
    onBlur,
    onValidationError,
    onValidationChange,
    onRegisterValidation,
    dataQsId,
  } = props

  const elementId = `dp-${name}`
  const errorId = `${elementId}-error`
  const readOnly = isLoading || propsReadOnly
  const formikContext = useFormik ? useFormikContext() : null
  const [errorMessage, setErrorMessage] = useState('')
  const pickerRef = useRef<HTMLInputElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const pendingSelectionRef = useRef<[number, number] | null>(null)
  const hasError = Boolean(errorMessage)

  let field = null
  let date = null
  if (formikContext && name) {
    field = formikContext.getFieldProps(name)
    date = field.value
  } else {
    date = value
  }

  const dateString = date && convertDateToApiDateFormat(date)
  const [textValue, setTextValue] = useState(dateString ? formatDate(dateString, null, format) : '')
  const textValueRef = useRef(textValue)
  const maxInputLength = format.length

  const updateTextValue = (newValue) => {
    setTextValue(newValue)
    textValueRef.current = newValue
  }

  useEffect(() => {
    if (!isLoading) {
      updateTextValue(dateString ? formatDate(dateString, null, format) : '')
      setErrorMessage('')
    }
  }, [isLoading, dateString, format])

  const classes = useStyles()

  const validateValue = (currentValue) => {
    if (!currentValue) {
      return { error: required ? lang.emptyRequiredFieldErrorMessage : '', apiValue: null }
    }

    const parsedDate = parseClientDate(currentValue, format)
    if (parsedDate.status !== 'valid') {
      return { error: lang.invalidDateErrorMessage, apiValue: null }
    }

    const min = minDate ? convertDateToApiDateFormat(minDate) : null
    const max = maxDate ? convertDateToApiDateFormat(maxDate) : null
    if ((min && parsedDate.value < min) || (max && parsedDate.value > max)) {
      return { error: lang.dateOutOfRangeErrorMessage, apiValue: null }
    }

    return { error: '', apiValue: parsedDate.value }
  }

  const updateModel = (newValue) => {
    formikContext?.setFieldValue(name, newValue)
    onChange?.(newValue, name)
  }

  const commitValue = (currentValue) => {
    const { error, apiValue } = validateValue(currentValue)

    setErrorMessage(error)
    onValidationChange?.(!error, name)

    if (error) {
      onValidationError?.(currentValue, name)
      return { isValid: false, apiValue: null }
    }

    if (apiValue) {
      updateTextValue(formatDate(apiValue, null, format))
      if (apiValue !== dateString) { updateModel(apiValue) }
    }

    return { isValid: true, apiValue }
  }

  const validateInputRef = useRef<() => boolean>(() => true)
  useEffect(() => {
    validateInputRef.current = () => commitValue(textValueRef.current).isValid
  })

  useEffect(() => {
    if (!onRegisterValidation || !name) { return undefined }
    onRegisterValidation(name, () => validateInputRef.current())
    return () => onRegisterValidation(name, null)
  }, [name, onRegisterValidation])

  useLayoutEffect(() => {
    const selection = pendingSelectionRef.current
    if (!selection || !inputRef.current) { return }

    pendingSelectionRef.current = null
    inputRef.current.setSelectionRange(selection[0], selection[1])
  })

  const handleTextChange = (event) => {
    const rawValue = event.target?.value || ''
    const newValue = rawValue
      .replace(/[^\d.]/g, '')
      .replace(/\.{2,}/g, '.')
      .slice(0, maxInputLength)

    const isDeletion = Boolean(event.nativeEvent?.inputType?.startsWith('delete'))
    const { value: advancedValue, selection } = !isDeletion && newValue === rawValue
      ? applySegmentAutoAdvance(newValue, event.target.selectionStart ?? newValue.length, format)
      : { value: newValue, selection: null }

    pendingSelectionRef.current = selection
    updateTextValue(advancedValue)
    setErrorMessage('')
    onValidationChange?.(!validateValue(advancedValue).error, name)
  }

  const handlePickerChange = (event) => {
    const newValue = event.target?.value
    if (!newValue) { return }

    updateTextValue(formatDate(newValue, null, format))
    setErrorMessage('')
    onValidationChange?.(true, name)
    updateModel(newValue)
  }

  const handleBlur = (event) => {
    const blurValue = event.target?.value || ''

    if (!blurValue && disableClear) {
      const todayValue = new Date().toISOString()
      updateTextValue(formatDate(todayValue, null, format))
      setErrorMessage('')
      onValidationChange?.(true, name)
      updateModel(todayValue)
      onBlur?.(todayValue, name)
      return
    }

    const { isValid, apiValue } = commitValue(blurValue)
    onBlur?.(isValid && apiValue ? apiValue : blurValue, name)
  }

  // Manual entry is committed exactly like on blur, so keyboard users can take over a typed date
  // without leaving the field first. The event is deliberately not cancelled: the ag-grid filter
  // popup wraps its inputs in a <form> whose submit button is the apply button — the implicit
  // form submission after this handler is what applies the filter immediately.
  const handleKeyDown = (event) => {
    if (event.key !== 'Enter') { return }
    commitValue(event.target?.value || '')
  }

  const handleFocus = () => {
    onFocus?.()
  }

  const handleMouseUp = (event) => {
    const input = event.target
    if (!input?.value || input.selectionStart !== input.selectionEnd) { return }

    const segments = Array.from(input.value.matchAll(/\d+/g), (match) => ({
      start: match.index,
      end: match.index + match[0].length,
    }))
    if (!segments.length) { return }

    const caretPosition = input.selectionStart
    const segment = segments.find(({ end }) => caretPosition <= end) || segments[segments.length - 1]
    input.setSelectionRange(segment.start, segment.end)
  }

  const openPicker = () => {
    const picker = pickerRef.current as HTMLInputElement & { showPicker?: () => void }
    if (!picker) { return }
    if (typeof picker.showPicker === 'function') {
      picker.showPicker()
      return
    }
    picker.focus()
  }

  const toolTipClasses = useMemo(() => ({ tooltip: clsx(classes.tooltip, tooltipClass) }), [classes.tooltip, tooltipClass])

  const labelEl = useMemo(() => {
    let labelEl = label ? <label htmlFor={elementId} className={classes.label}>{label}</label> : null
    if (labelTooltip && labelEl) {
      const tooltipEl = (
        <>
          {labelTooltip.showInfoIcon && labelEl}
          <RichTooltip heading={labelTooltip?.title} body={labelTooltip?.description} width='500px' placement={labelTooltip.placement || 'right'}>
            {labelTooltip.showInfoIcon ? <SVGIcon width={14} height={14} svg={IconInfo} /> : labelEl}
          </RichTooltip>
        </>
      )
      labelEl = labelTooltip.showInfoIcon ? <div className={classes.tooltipWithInfoIcon}>{tooltipEl}</div> : tooltipEl
    }

    return labelEl
  }, [classes.label, classes.tooltipWithInfoIcon, elementId, label, labelTooltip])

  return (
    <div className={clsx(classes.container, customClass, className)} data-qs-id={dataQsId}>
      {labelEl}
      <Tooltip title={tooltip} classes={toolTipClasses}>
        <div>
          <FillTransition {...fillAnimationOptions}>
            <div className={classes.inputWrapper}>
              <input
                ref={inputRef}
                id={elementId}
                type='text'
                className={clsx(classes.textInput, inline && classes.inline, tall && classes.textInputTall, readOnly && classes.disabled, hasError && classes.textInputError)}
                value={textValue}
                placeholder={placeholder}
                maxLength={maxInputLength}
                inputMode='numeric'
                disabled={readOnly}
                aria-invalid={hasError}
                aria-describedby={hasError ? errorId : undefined}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onMouseUp={handleMouseUp}
                onKeyDown={handleKeyDown}
                onChange={handleTextChange} />
              <input
                ref={pickerRef}
                type='date'
                tabIndex={-1}
                aria-hidden='true'
                className={classes.visuallyHiddenPicker}
                disabled={readOnly}
                value={dateString || ''}
                min={minDate ? convertDateToApiDateFormat(minDate) : undefined}
                max={maxDate ? convertDateToApiDateFormat(maxDate) : undefined}
                onChange={handlePickerChange} />
              <button
                type='button'
                aria-label={lang.openDatePickerLabel}
                className={classes.pickerButton}
                disabled={readOnly}
                onClick={openPicker}>
                <SVGIcon width={16} height={16} svg={IconCalendar} />
              </button>
            </div>
          </FillTransition>
          {hasError && <div id={errorId} className={classes.error} role='alert'>{errorMessage}</div>}
        </div>
      </Tooltip>
    </div>
  )
}

export default DateInput
